import React, { ButtonHTMLAttributes, cloneElement, forwardRef } from 'react';

import { cn } from '~/utils/styles';

import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      children,
      className,
      disabled,
      asChild = false,
      isLoading = false,
      ...props
    },
    ref,
  ) => {
    const baseClasses =
      'inline-flex items-center justify-center rounded-md font-medium ring-offset-white transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer';

    const variantClasses = {
      // Primary: Use existing theme primary color
      primary:
        'bg-primary text-white hover:bg-primary-light active:bg-primary-dark shadow-md hover:shadow-lg',
      // Secondary: Use gray shade instead of yellow
      secondary:
        'bg-gray-200 text-gray-800 hover:bg-gray-300 active:bg-gray-400 shadow-md hover:shadow-lg',
      // Outline: Clean white with primary border
      outline: 'border-2 border-primary bg-white text-primary hover:bg-gray-50 active:bg-gray-100',
      // Ghost: Subtle hover effect
      ghost: 'text-primary hover:bg-gray-50 active:bg-gray-100',
      // Danger: Use bronze color for logout (more thematic than red)
      danger:
        'bg-bronze text-white hover:bg-bronze/90 active:bg-bronze/80 shadow-md hover:shadow-lg',
      // Success: Green for positive actions
      success:
        'bg-green-600 text-white hover:bg-green-500 active:bg-green-700 shadow-md hover:shadow-lg',
      // Warning: Orange for cautionary actions
      warning:
        'bg-orange-500 text-white hover:bg-orange-400 active:bg-orange-600 shadow-md hover:shadow-lg',
    };

    const sizeClasses = {
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4 py-2',
      lg: 'h-12 px-6 text-lg',
    };

    const buttonClasses = cn(baseClasses, variantClasses[variant], sizeClasses[size], className);

    const isDisabled = disabled || isLoading;

    if (asChild) {
      const child = children as React.ReactElement<{ className?: string }>;

      return cloneElement(child, {
        className: cn(buttonClasses, child.props.className),
        ref,
      } as React.Attributes & { className?: string; ref?: React.Ref<unknown> });
    }

    return (
      <button ref={ref} className={buttonClasses} disabled={isDisabled} {...props}>
        {isLoading && <LoadingSpinner size="sm" className="mr-2" />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button };
